<script setup lang="ts">
import { PhxCoreWizard } from '@phx-styleguides/core';
import { ref, computed, nextTick, watch, onMounted, onUnmounted, Ref, ComputedRef } from 'vue';

import { AnimateGroupReveal } from '@sg-core/resources/assets/js/animations';
import { useKeydown } from '@sg-core/resources/assets/js/composables';
import { restrictScroll, unleashScroll, debounce } from '@sg-core/resources/assets/js/utils';

interface IwcTransactionPanelProps {
  initialStep?: number;
}

const { initialStep = 0 } = defineProps<IwcTransactionPanelProps>();

const rootRef: Ref<HTMLElement | null> = ref(null);
const wizardRef: Ref<InstanceType<typeof PhxCoreWizard> | null> = ref(null);

const isOpen: ComputedRef<boolean> = computed(() => {
  const steps = wizardRef.value?.steps;
  return steps?.current ? steps.current > 0 : false;
});

const closePanel = (): void => {
  wizardRef.value?.goToFirstStep();
};

useKeydown({
  Escape: () => {
    if (isOpen.value) {
      closePanel();
    }
  },
});

const setFirstStepHeight = (): void => {
  if (!wizardRef.value || !wizardRef.value.$el) {
    return;
  }

  const firstStep = wizardRef.value.$el.querySelector<HTMLElement>('.wizard-step');

  if (firstStep) {
    const rect = firstStep.getBoundingClientRect();
    document.documentElement.style.setProperty('--transaction-panel-base-height', `${rect.height}px`);
  }
};

const debouncedSetFirstStepHeight = debounce(setFirstStepHeight, 200);

onMounted((): void => {
  window.addEventListener('resize', debouncedSetFirstStepHeight);
});

onUnmounted((): void => {
  window.removeEventListener('resize', debouncedSetFirstStepHeight);
  unleashScroll();
});

watch(isOpen, (newValue: boolean): void => {
  if (newValue) {
    restrictScroll();
  } else {
    unleashScroll();
  }
});

watch(
  () => wizardRef.value?.$el,
  (newEl: Element | undefined): void => {
    if (newEl) {
      nextTick(() => {
        setFirstStepHeight();
      });
    }
  },
);

watch(rootRef, (newValue) => {
  nextTick(() => {
    new AnimateGroupReveal({
      parentElement: newValue ?? undefined,
      childrenSelector: '.phxsg-fade-in, .phxsg-mask-reveal-to-right, .phxsg-mask-reveal-to-top',
      delay: 0,
      initialDelay: 1.5,
      gsapOptions: {
        delay: 1,
      },
    });
  });
});

defineExpose({
  wizardRef,
});
</script>

<template>
  <div
    ref="rootRef"
    class="transaction-panel center center--spacing-3 center--sizing-3xl section section--spacing-4"
    :class="{
      'transaction-panel--active': isOpen,
    }"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @click.self="closePanel"
  >
    <PhxCoreWizard ref="wizardRef" :initial-step="initialStep" class="transaction-panel__content">
      <template #default="slotProps">
        <slot v-bind="slotProps" />
      </template>
    </PhxCoreWizard>
  </div>
</template>
